import { useEffect, useState } from "react";
import { api, brl, STATUS_LABEL, STATUS_COLOR } from "../lib/api";
import { Card } from "../components/ui/card";
import { BarChart, Bar, Cell, LabelList, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowUpRight, TrendingUp, Clock, DollarSign, Target } from "lucide-react";

export default function Dashboard() {
  const [a, setA] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/analytics").then((r) => setA(r.data));
    api.get("/proposals").then((r) => setRecent(r.data.slice(0, 5)));
  }, []);

  if (!a) return <div className="p-10 overline">Carregando…</div>;

  const metrics = [
    { label: "Taxa de Conversão", value: `${a.conversion_rate}%`, icon: Target, testid: "metric-conversion" },
    { label: "Faturamento Aprovado", value: brl(a.revenue), icon: DollarSign, testid: "metric-revenue", gold: true },
    { label: "Ticket Médio", value: brl(a.avg_ticket), icon: TrendingUp, testid: "metric-avg-ticket" },
    { label: "Tempo até Aprovação", value: `${a.avg_time_hours}h`, icon: Clock, testid: "metric-time" },
  ];

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline">Visão Geral</p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2">Painel Executivo</h1>
          <p className="text-sm text-muted-foreground mt-2">Visão consolidada de propostas, conversão e projeções.</p>
        </div>
        <Button asChild className="bg-[#C07C88] hover:bg-[#A86571] rounded-md" data-testid="new-proposal-cta">
          <Link to="/proposals/new">Nova Proposta <ArrowUpRight size={14} className="ml-1" /></Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((m) => (
          <Card key={m.label} className="p-6 border-border shadow-none" data-testid={m.testid}>
            <div className="flex items-start justify-between">
              <p className="overline">{m.label}</p>
              <m.icon size={16} strokeWidth={1.5} className={m.gold ? "text-[#D4AF37]" : "text-muted-foreground"} />
            </div>
            <p className={`font-heading text-3xl mt-3 ${m.gold ? "text-[#D4AF37]" : ""}`}>{m.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <Card className="lg:col-span-2 p-6 border-border shadow-none">
          <p className="overline mb-4">Faturamento por mês</p>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={a.monthly}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `R$${v / 1000}k`} />
                <Tooltip formatter={(v) => brl(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} />
                <Line type="monotone" dataKey="count" stroke="#C07C88" strokeWidth={2} dot={false} yAxisId={0} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 border-border shadow-none">
          <p className="overline mb-4">Distribuição por status</p>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={["draft","sent","viewed","approved","rejected","expired"].map((k) => ({ status: STATUS_LABEL[k], count: a.status_counts?.[k] || 0, color: STATUS_COLOR[k] }))} margin={{ top: 20, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="status" fontSize={10} stroke="#71717A" interval={0} />
                <YAxis fontSize={11} stroke="#71717A" allowDecimals={false} />
                <Tooltip cursor={{ fill: "#FDF9EE" }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]} minPointSize={2}>
                  <LabelList dataKey="count" position="top" fontSize={11} fill="#1A1A1A" fontWeight={600} />
                  {["draft","sent","viewed","approved","rejected","expired"].map((k) => (
                    <Cell key={k} fill={STATUS_COLOR[k]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="border-border shadow-none">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <p className="overline">Propostas recentes</p>
          <Link to="/proposals" className="text-xs text-[#C07C88] hover:underline" data-testid="see-all-proposals">Ver todas →</Link>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-4">Nº</th>
              <th className="p-4">Cliente</th>
              <th className="p-4">Valor</th>
              <th className="p-4">Status</th>
              <th className="p-4">Data</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((p) => (
              <tr key={p.proposal_id} className="border-t border-border hover:bg-[#FAFAFA]">
                <td className="p-4 font-mono text-xs">{p.number}</td>
                <td className="p-4">{p.client_name}</td>
                <td className="p-4 font-medium">{brl(p.totals.total)}</td>
                <td className="p-4">
                  <span className="inline-flex items-center text-xs font-medium">
                    <span className="status-dot" style={{ background: STATUS_COLOR[p.status] }} />
                    {STATUS_LABEL[p.status]}
                  </span>
                </td>
                <td className="p-4 text-xs text-muted-foreground">{p.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-sm text-muted-foreground">Nenhuma proposta ainda. <Link to="/proposals/new" className="text-[#C07C88] underline">Criar a primeira</Link></td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
